import React, { useState, useEffect, useCallback } from 'react'
import {
  fetchAccount,
  saveAccount,
  fetchApps,
  createApp,
  deleteApp,
  fetchAppAgents,
  provisionAppAgent,
  deprovisionAppAgent,
  registerAppWebhooks,
} from '../services/telephonyApi'
import { getErrorMessage } from '../services/telephonyHelpers'

import TelephonySettingsHeader from './components/TelephonySettingsHeader'
import AccountCredentialsForm from './components/AccountCredentialsForm'
import AppRegistryCard from './components/AppRegistryCard'
import AgentProvisioningTable from './components/AgentProvisioningTable'
import ProvisionAgentModal from './components/ProvisionAgentModal'

/**
 * TelephonySettingsScreen
 * -----------------------
 * Streamlined 3-Component Layout (Enterprise System Design):
 *   1. Account credentials — tenant-level Exotel credentials with atomic validation on save.
 *   2. Apps Registry       — Multi-App registrations with app-level deletion guard.
 *   3. Agents Provisioning — employees left-joined with agent mappings, modal-driven provisioning,
 *                            safe deprovisioning with appConfirm, and read-only PSTN numbers.
 */
export default function TelephonySettingsScreen() {
  // ── Account ──────────────────────────────────────────────────────────
  const [account, setAccount] = useState({
    accountSid: '',
    exotelApiKey: '',
    exotelApiToken: '',
    exotelDomain: 'Mumbai',
    customerId: '',
    customerSecret: '',
    exophone: '',
    customerTokenExpiresAt: null,
  })
  const [accountLoading, setAccountLoading] = useState(true)
  const [savingAccount, setSavingAccount] = useState(false)
  const [accountMsg, setAccountMsg] = useState(null)

  // ── Apps ─────────────────────────────────────────────────────────────
  const [apps, setApps] = useState([])
  const [appsLoading, setAppsLoading] = useState(true)
  const [selectedAppId, setSelectedAppId] = useState(null)
  const [creatingApp, setCreatingApp] = useState(false)
  const [appMsg, setAppMsg] = useState(null)

  // ── Selected app: agents ─────────────────────────────────────────────
  const [agents, setAgents] = useState([])
  const [agentsLoading, setAgentsLoading] = useState(false)
  const [provisionMsg, setProvisionMsg] = useState(null)

  // ── Provision Modal state ────────────────────────────────────────────
  const [provisionModalEmp, setProvisionModalEmp] = useState(null)
  const [provisioningModal, setProvisioningModal] = useState(false)

  const selectedApp = apps.find((a) => a.id === selectedAppId) || null

  // ── Loaders ──────────────────────────────────────────────────────────
  const loadAccount = useCallback(async () => {
    setAccountLoading(true)
    try {
      const res = await fetchAccount()
      if (res?.success && res.data) {
        const d = res.data
        setAccount((prev) => ({
          ...prev,
          accountSid: d.account_sid || '',
          exotelApiKey: d.exotel_api_key || '',
          exotelApiToken: d.exotel_api_token || '',
          exotelDomain: d.exotel_domain || 'Mumbai',
          customerId: d.customer_id || '',
          customerSecret: d.customer_secret || '',
          exophone: d.exophone_number || '',
          customerTokenExpiresAt: d.customer_token_expires_at || null,
        }))
      }
    } catch (err) {
      console.warn('[Telephony] account load failed:', err)
    } finally {
      setAccountLoading(false)
    }
  }, [])

  const loadApps = useCallback(async (preferId = null) => {
    setAppsLoading(true)
    try {
      const res = await fetchApps()
      const items = Array.isArray(res?.data) ? res.data : []
      setApps(items)
      setSelectedAppId((prev) => {
        if (preferId && items.some((a) => a.id === preferId)) return preferId
        if (prev && items.some((a) => a.id === prev)) return prev
        return items.length ? items[0].id : null
      })
    } catch (err) {
      console.warn('[Telephony] apps load failed:', err)
      setApps([])
    } finally {
      setAppsLoading(false)
    }
  }, [])

  const loadAgents = useCallback(async (appId) => {
    if (!appId) return
    setAgentsLoading(true)
    try {
      const res = await fetchAppAgents(appId)
      setAgents(Array.isArray(res?.data) ? res.data : [])
    } catch (err) {
      console.warn('[Telephony] agents load failed:', err)
      setAgents([])
    } finally {
      setAgentsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadAccount()
    loadApps()
  }, [loadAccount, loadApps])

  // Reload selected app's agents whenever selection changes
  useEffect(() => {
    if (selectedAppId) {
      setProvisionMsg(null)
      loadAgents(selectedAppId)
    } else {
      setAgents([])
    }
  }, [selectedAppId, loadAgents])

  // ── Account handlers ─────────────────────────────────────────────────
  const handleSaveAccount = async (e) => {
    e.preventDefault()
    setSavingAccount(true)
    setAccountMsg(null)
    try {
      await saveAccount({
        account_sid: account.accountSid,
        exotel_api_key: account.exotelApiKey || undefined,
        exotel_api_token: account.exotelApiToken || undefined,
        exotel_domain: account.exotelDomain,
        customer_id: account.customerId || undefined,
        customer_secret: account.customerSecret || undefined,
        exophone_number: account.exophone || undefined,
      })
      await loadAccount()
      setAccountMsg({ type: 'success', text: 'Credentials validated with Exotel and saved successfully.' })
    } catch (err) {
      setAccountMsg({ type: 'error', text: getErrorMessage(err, 'Could not save credentials') })
    } finally {
      setSavingAccount(false)
    }
  }

  // ── App handlers ─────────────────────────────────────────────────────
  const handleCreateApp = async (payload) => {
    setCreatingApp(true)
    setAppMsg(null)
    try {
      const res = await createApp(payload)
      const newId = res?.data?.id
      await loadApps(newId)
      setAppMsg({ type: 'success', text: `App "${payload.app_name}" created.` })
      return true
    } catch (err) {
      setAppMsg({ type: 'error', text: getErrorMessage(err, 'Could not create app') })
      return false
    } finally {
      setCreatingApp(false)
    }
  }

  const handleDeleteApp = async (appId) => {
    setAppMsg(null)
    try {
      await deleteApp(appId)
      const remaining = apps.filter((a) => a.id !== appId)
      await loadApps(remaining.length ? remaining[0].id : null)
      setAppMsg({ type: 'success', text: 'App deleted.' })
    } catch (err) {
      setAppMsg({ type: 'error', text: getErrorMessage(err, 'Could not delete app') })
    }
  }

  /**
   * Re-register Exotel webhooks for a given app with a custom base URL.
   * Used when the backend is behind ngrok (dev) or a different production domain.
   * Exotel will POST popup / callback / incomingCallHangup / missedCall to:
   *   {baseUrl}/telephony/webhook/popup   and
   *   {baseUrl}/telephony/webhook/status
   */
  const handleReregisterWebhooks = async (appId, baseUrl) => {
    const res = await registerAppWebhooks(appId, { base_url: baseUrl })
    if (res?.success) {
      await loadApps(appId)
    }
    return res
  }

  // ── Agent handlers ───────────────────────────────────────────────────
  const handleOpenProvisionModal = (emp) => {
    setProvisionMsg(null)
    setProvisionModalEmp(emp)
  }

  const handleConfirmProvision = async (targetAppId, emp, customAppUserId) => {
    const empUserId = emp.id || emp.user_id
    const empEmail = customAppUserId || emp.email || emp.app_user_id
    setProvisioningModal(true)
    setProvisionMsg(null)
    try {
      const res = await provisionAppAgent(targetAppId, {
        user_id: empUserId,
        app_user_id: empEmail,
      })
      await Promise.all([loadAgents(selectedAppId), loadApps(selectedAppId)])
      setProvisionMsg({
        type: 'success',
        text: res?.message || `${emp.employee_name || emp.name || empEmail} provisioned successfully.`,
      })
      setProvisionModalEmp(null)
    } catch (err) {
      setProvisionMsg({ type: 'error', text: getErrorMessage(err, 'Provisioning failed') })
    } finally {
      setProvisioningModal(false)
    }
  }

  const handleDeprovisionAgent = async (empUserId) => {
    if (!selectedAppId) return
    setProvisionMsg(null)
    try {
      await deprovisionAppAgent(selectedAppId, empUserId)
      await Promise.all([loadAgents(selectedAppId), loadApps(selectedAppId)])
      setProvisionMsg({ type: 'success', text: 'Agent deprovisioned from this app.' })
    } catch (err) {
      setProvisionMsg({ type: 'error', text: getErrorMessage(err, 'Deprovisioning failed') })
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-24">
      <TelephonySettingsHeader />

      <div className="space-y-6">
        {/* 1. Account Credentials Form */}
        <AccountCredentialsForm
          account={account}
          setAccount={setAccount}
          loading={accountLoading}
          saving={savingAccount}
          saveMsg={accountMsg}
          setSaveMsg={setAccountMsg}
          onSave={handleSaveAccount}
        />

        {/* 2. Apps Registry */}
        <AppRegistryCard
          apps={apps}
          appsLoading={appsLoading}
          selectedAppId={selectedAppId}
          onSelectApp={setSelectedAppId}
          creating={creatingApp}
          appMsg={appMsg}
          setAppMsg={setAppMsg}
          onCreateApp={handleCreateApp}
          onDeleteApp={handleDeleteApp}
          onReregisterWebhooks={handleReregisterWebhooks}
        />

        {/* 3. Agents Provisioning Table */}
        {selectedApp ? (
          <AgentProvisioningTable
            app={selectedApp}
            apps={apps}
            selectedAppId={selectedAppId}
            onSelectApp={setSelectedAppId}
            exophone={account.exophone}
            employees={agents}
            employeesLoading={agentsLoading}
            provisioningId={provisioningModal ? (provisionModalEmp?.id || provisionModalEmp?.user_id) : null}
            provisionMsg={provisionMsg}
            setProvisionMsg={setProvisionMsg}
            onRequestProvision={handleOpenProvisionModal}
            onDeprovisionAgent={handleDeprovisionAgent}
          />
        ) : (
          !appsLoading && (
            <div className="bg-surface-container-lowest p-10 rounded-2xl border border-dashed border-outline-variant/40 text-center">
              <span className="material-symbols-outlined text-3xl text-on-surface-variant">apps</span>
              <p className="text-xs text-on-surface-variant mt-2">
                Select or create an app to view and manage provisioned agents.
              </p>
            </div>
          )
        )}
      </div>

      {/* Modal for Provisioning Agent with Target App Selector */}
      <ProvisionAgentModal
        open={Boolean(provisionModalEmp)}
        onClose={() => setProvisionModalEmp(null)}
        employee={provisionModalEmp}
        apps={apps}
        defaultAppId={selectedAppId}
        onConfirm={handleConfirmProvision}
        provisioning={provisioningModal}
      />
    </div>
  )
}
